/**
 * Abono de precio fijo (`discounts.fixed_price`).
 * Gemelo de `utils/fixed-price-pass.util.ts` de la web: si cambia uno, cambiar el otro.
 *
 * Cada sesión del abono tiene su propia entrada («Abono - nombre») con un precio
 * por nivel del mapa. El abono trae esos precios en `pass_prices` (uno por
 * sesión y nivel; `level_id` null en los eventos sin mapa) y su resumen en
 * `price_summary`. Un nivel sin precio no entra en el abono en esa sesión.
 * El back es quien manda: pone la entrada del abono en cada línea y rechaza las
 * butacas de niveles sin precio. Aquí solo se usa para enseñar precios y para
 * no dejar vender una butaca de un nivel que no entra en el abono.
 */

export function isFixedPricePass(pass: any): boolean {
  return !!pass?.fixed_price;
}

/** Fila de `pass_prices` de una sesión y nivel (`levelId` = PK de map_levels; null sin mapa), o null si no entra. */
export function fixedPassPriceRow(pass: any, sessionId: number, levelId: number | null): any | null {
  return (pass?.pass_prices || []).find((p: any) =>
    +p.session_id === +sessionId && (levelId == null ? p.level_id == null : +p.level_id === +levelId)
  ) || null;
}

/** Precio del abono en una sesión y nivel, o null si ese nivel no entra. */
export function fixedPassPrice(pass: any, sessionId: number, levelId: number | null): number | null {
  const row = fixedPassPriceRow(pass, sessionId, levelId);
  return row ? +row.price : null;
}

/** «Desde 34,00 €», o «Precio fijo» si el back no trae el resumen. */
export function fixedPassFromLabel(pass: any): string {
  const from = pass?.price_summary?.from;
  if (from == null) return 'Precio fijo';
  return `Desde ${(+from).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * Opciones de entrada en la venta de un abono de precio fijo: una por nivel con
 * precio, que es la entrada del abono en esa sesión con el precio del nivel. Las
 * entradas del evento solo dicen qué niveles hay: llegan ya calculadas para un
 * nivel (`levelId`) o, en la selección masiva, cada una con su `_levelId`.
 */
export function fixedPassOptions(pass: any, sessionId: number, tickets: any[], levelId?: number | null): any[] {
  const levels = new Map<string, any>();
  (tickets || []).forEach((t: any) => {
    const level = t._levelId !== undefined ? t._levelId : (levelId ?? null);
    const key = level == null ? 'none' : `${level}`;
    if (!levels.has(key)) levels.set(key, t);
  });
  if (levels.size === 0) levels.set(levelId == null ? 'none' : `${levelId}`, null);

  const options: any[] = [];
  levels.forEach((sample, key) => {
    const row = fixedPassPriceRow(pass, sessionId, key === 'none' ? null : +key);
    if (!row) return;

    options.push({
      ...(sample || {}),
      id: row.ticket_id ?? null,
      name: pass?.public_name || pass?.name || 'Entrada del abono',
      price: +row.price,
      max_tickets: null,
      only_tpv_sales: false,
      fixed_pass_price: true,
    });
  });

  return options;
}
